// @flow
import { PlusCircle } from '@phosphor-icons/react'
import { Button } from 'antd'
import map from 'lodash/map'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import { Droppable } from 'react-beautiful-dnd'
import type { Page } from 'types/page'
import { CONSTANTS_FORM_BUILDER } from 'utils/FormBuilder/ConstantsFormBuilder'
import { hasText } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'

import BuilderAddButton from './BuilderAddButton'
import { BuilderHeaderRepeatSection } from './BuilderHeaderRepeatSection/BuilderHeaderRepeatSection'
import BuilderSection from './BuilderSection'
import BuilderSelectableItem from './BuilderSelectableItem'

type Props = {
  page: Page,
  index: number,
  className?: string,
  depth?: number,
  isChild?: boolean,
}

function BuilderPageTree({
  page,
  index,
  className,
  depth = 0,
  isChild = false,
}: Props): React.Node {
  const { getSectionsForPage } = React.useContext(FormBuilderContext)

  const isRepeatingSection = Boolean(page?.repeat_section)
  const hasChildren = page?.children && page.children.length > 0

  const hasMoveUp = depth ? true : index !== 0
  const hasMoveDown = true

  const pageSections = getSectionsForPage(page.id)
  const parentPageIdByPage = page?.id

  const shouldShowAddChildButton = React.useMemo(() => {
    if (!hasChildren) return false

    const lastChild = page.children[page.children.length - 1]

    return Boolean(lastChild?.repeat_section)
  }, [hasChildren, page.children])

  return (
    <div
      className={cn(
        'relative rounded border !border-gray-300 bg-white p-6 pb-4 [&:not(:first-child)]:mt-6',
        {
          'mt-12 !border-dashed pt-3 transition duration-150 hover:!bg-[#f7fcff] [&:hover_.repeating-section-title]:!bg-[#f7fcff]':
            isRepeatingSection,
        },
        className
      )}
    >
      {isRepeatingSection ? (
        <BuilderHeaderRepeatSection
          page={page}
          type={CONSTANTS_FORM_BUILDER.SELECTION_TYPE.PAGE}
        />
      ) : null}

      {!isChild && !isRepeatingSection ? (
        <BuilderSelectableItem
          className="mb-2"
          dataCy="section-title"
          hasMoveDown={hasMoveDown}
          hasMoveUp={hasMoveUp}
          id={page.id}
          type={CONSTANTS_FORM_BUILDER.SELECTION_TYPE.PAGE}
        >
          <p
            className={cn('cursor-default px-3 py-1 text-3xl text-gray-300', {
              'text-gray-700': hasText(page?.title),
            })}
          >
            {page?.title || 'Section title'}
          </p>
        </BuilderSelectableItem>
      ) : null}

      {map(pageSections, (section, i) => (
        <BuilderSection key={section.id} sectionId={section.id} />
      ))}

      {hasChildren && (
        <>
          <div className="mt-4">
            {map(page.children, (childPage, childIndex) => (
              <BuilderPageTree
                className={cn({
                  '!border-none !p-0': !childPage.repeat_section,
                })}
                depth={depth + 1}
                index={childIndex}
                isChild={true}
                key={childPage.id}
                page={childPage}
              />
            ))}
          </div>
          {shouldShowAddChildButton && (
            <Droppable
              isCombineEnabled
              droppableId={`addChildPageTo_${page.id}`}
              type="TASK"
            >
              {(provided, snapshot) => (
                <div
                  className="-mx-1 grid grid-cols-1 pt-1 sm:grid-cols-12"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <BuilderAddButton
                    isSelectableItem
                    containerClassNames="px-1"
                    id={page.id}
                    isHighlighted={snapshot.isDraggingOver}
                    parentPageId={parentPageIdByPage}
                    spacingTop={0}
                    type={CONSTANTS_FORM_BUILDER.SELECTION_TYPE.ROW_ADD}
                  >
                    +
                  </BuilderAddButton>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </>
      )}

      {isRepeatingSection ? (
        <div className="mt-4 flex">
          <Button
            className="pointer-events-none opacity-45"
            icon={<PlusCircle className="text-xl" weight="bold" />}
            type="primary"
          >
            Add Item
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default BuilderPageTree
